import { useState, useEffect } from 'react'
import { Strategy } from '@/types'
import { 
  Plus, 
  Search, 
  Filter, 
  TrendingUp, 
  BarChart3,
  Calendar,
  Eye,
  Eye as EyeIcon,
  Play,
  Rocket,
  AlertCircle
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getUserStrategies } from '../lib/api'
import Navigation from '../components/Navigation'

export default function HomePage() {
  const navigate = useNavigate()
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [stats, setStats] = useState({
    total: 0,
    live: 0,
    test: 0,
    draft: 0
  })

  // 加载策略数据
  const loadStrategies = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const data = await getUserStrategies({
        search: searchTerm || undefined,
        type: filterType !== 'all' ? filterType : undefined,
        limit: 50
      })
      
      setStrategies(data.strategies || [])
      setStats({
        total: data.stats?.total || 0,
        live: data.stats?.live || 0,
        test: data.stats?.test || 0,
        draft: data.stats?.draft || 0
      })
    } catch (err) {
      console.error('Error loading strategies:', err)
      setError('加载策略数据失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  // 初始加载
  useEffect(() => {
    loadStrategies()
  }, [])

  // 搜索和筛选变化时重新加载
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadStrategies()
    }, 300) // 防抖

    return () => clearTimeout(timeoutId)
  }, [searchTerm, filterType])

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      trend: '趋势跟踪',
      grid: '网格交易',
      mean_reversion: '均值回归',
      breakout: '突破策略',
      scalping: '高频交易',
      other: '其他'
    }
    return typeMap[type] || type
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: '草稿',
      test: '验证中',
      live: '实盘中'
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'test':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  // 移除本地过滤，因为现在使用后端过滤
  const filteredStrategies = strategies

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-400"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* 页面标题 */}
        <div className="card mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gradient mb-2">策略管理</h2>
          <p className="text-sm sm:text-base text-white/70">管理和监控您的交易策略</p>
        </div>

        {/* 搜索和筛选 */}
        <div className="card mb-4 sm:mb-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* 搜索框 - 始终显示 */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索策略名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3"
              />
            </div>
            
            {/* 筛选器 - 小屏幕下隐藏，大屏幕显示 */}
            <div className="hidden sm:flex items-center space-x-2">
              <Filter className="w-4 h-4 text-white/50" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-3"
              >
                <option value="all">所有类型</option>
                <option value="trend">趋势跟踪</option>
                <option value="grid">网格交易</option>
                <option value="mean_reversion">均值回归</option>
                <option value="breakout">突破策略</option>
                <option value="scalping">高频交易</option>
                <option value="other">其他</option>
              </select>
            </div>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="card mb-4 sm:mb-6">
            <div className="flex items-center p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* 统计卡片 */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-ocean-secondary">总策略数</p>
                <p className="text-xl sm:text-2xl font-bold text-gradient">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-green-500/20 rounded-xl border border-green-500/30">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-ocean-secondary">实盘中</p>
                <p className="text-xl sm:text-2xl font-bold text-gradient">{stats.live}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                <Calendar className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">验证中</p>
                <p className="text-2xl font-bold text-gradient">{stats.test}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-gray-500/20 rounded-xl border border-gray-500/30">
                <Eye className="w-6 h-6 text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">草稿</p>
                <p className="text-2xl font-bold text-gradient">{stats.draft}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 策略列表 */}
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredStrategies.map((strategy) => (
            <div key={strategy.id} className="card hover:scale-105 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-ocean-primary mb-2">
                    {strategy.name}
                  </h3>
                  <p className="text-sm text-ocean-secondary mb-3">
                    {strategy.description}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(strategy.status)}`}>
                  {getStatusLabel(strategy.status)}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-ocean-secondary">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {getTypeLabel(strategy.type)}
                </div>
                <div className="flex items-center text-sm text-ocean-secondary">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {strategy.market_symbol} • {strategy.timeframe}
                </div>
                <div className="flex items-center text-sm text-ocean-secondary">
                  <Calendar className="w-4 h-4 mr-2" />
                  创建于 {new Date(strategy.created_at).toLocaleDateString('zh-CN')}
                </div>
              </div>

              <div className="mt-4 flex space-x-2">
                <button 
                  onClick={() => navigate(`/strategy/${strategy.id}`)}
                  className="flex-1 btn-secondary text-sm flex items-center justify-center"
                  title="查看详情"
                >
                  <EyeIcon className="w-4 h-4 mr-2 sm:mr-2" />
                  <span className="hidden sm:inline">查看详情</span>
                </button>
                <button 
                  className="flex-1 btn-primary text-sm flex items-center justify-center"
                  title="运行回测"
                >
                  <Play className="w-4 h-4 mr-2 sm:mr-2" />
                  <span className="hidden sm:inline">运行回测</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredStrategies.length === 0 && (
          <div className="empty-state">
            <BarChart3 className="w-16 h-16 text-ocean-secondary mx-auto mb-4" />
            <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无策略</h3>
            <p className="text-ocean-secondary mb-6">开始创建您的第一个交易策略</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/strategy/new')}
                className="px-4 sm:px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border border-blue-400/30 hover:border-blue-300/50 transform hover:-translate-y-0.5 hover:shadow-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%)',
                  boxShadow: '0 4px 12px 0 rgba(14, 165, 233, 0.3), 0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  zIndex: 1,
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale'
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">新建策略</span>
              </button>
              <button 
                onClick={() => navigate('/demo-login')}
                className="px-4 sm:px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white border border-green-400/30 hover:border-green-300/50 transform hover:-translate-y-0.5 hover:shadow-xl flex items-center justify-center"
              >
                <Rocket className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">体验Demo</span>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
} 